

import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { User, NewUserData, UpdateUserData, UserRole } from '../types';
import { UserIcon } from './icons/UserIcon';
import { XMarkIcon } from './icons/XMarkIcon';
import { useAuth } from '../hooks/useAuth';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: NewUserData | UpdateUserData) => void;
  initialUser?: User | null;
}

const initialFormState: NewUserData = {
  name: '',
  email: '',
  phone: '',
  password: '',
  role: UserRole.EMPLOYEE,
  avatarUrl: '',
};

export const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, onSubmit, initialUser }) => {
  const { user: loggedInUser } = useAuth();
  const isEditing = !!initialUser;
  const [formData, setFormData] = useState<NewUserData | UpdateUserData>(initialFormState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && initialUser) {
      setFormData({
        name: initialUser.name,
        email: initialUser.email,
        phone: initialUser.phone || '',
        password: '', // Password should be empty for editing unless changed
        role: initialUser.role,
        avatarUrl: initialUser.avatarUrl || '',
      });
    } else {
      setFormData(initialFormState);
    }
  }, [initialUser, isEditing, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, avatarUrl: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setFormData(prev => ({ ...prev, avatarUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      alert('Nome e E-mail são obrigatórios.');
      return;
    }
    if (!isEditing && !formData.password) {
        alert('Senha é obrigatória para novos usuários.');
        return;
    }
    onSubmit(formData);
  };
  
  const baseRoleOptions = [
    { value: UserRole.EMPLOYEE, label: 'Funcionário' },
    { value: UserRole.ADMIN, label: 'Administrador' },
  ];

  const roleOptions = loggedInUser?.role === UserRole.DEV
    ? [...baseRoleOptions, { value: UserRole.DEV, label: 'Desenvolvedor' }]
    : baseRoleOptions;
  
  const title = isEditing ? `Editar Usuário: ${initialUser?.name}` : 'Adicionar Novo Usuário';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Avatar Section */}
        <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 h-20 w-20 rounded-full bg-dark-tertiary flex items-center justify-center overflow-hidden">
                {formData.avatarUrl ? (
                    <img src={formData.avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                    <UserIcon className="text-5xl text-dark-text-secondary" />
                )}
            </div>
            <div className="flex flex-col space-y-2">
                <input
                    type="file"
                    id="avatar-upload"
                    ref={fileInputRef}
                    onChange={handleAvatarChange}
                    accept="image/*"
                    className="hidden"
                />
                <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
                    Alterar Foto
                </Button>
                {formData.avatarUrl && (
                    <Button type="button" variant="ghost" size="sm" onClick={removeAvatar} leftIcon={<XMarkIcon className="text-base"/>}>
                        Remover Foto
                    </Button>
                )}
            </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Nome Completo*" name="name" value={formData.name} onChange={handleChange} required />
          <Input label="E-mail*" name="email" type="email" value={formData.email} onChange={handleChange} required />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Telefone" name="phone" type="tel" value={formData.phone || ''} onChange={handleChange} />
          <Select label="Perfil de Usuário*" name="role" value={formData.role} onChange={handleChange} options={roleOptions} required />
        </div>
        <Input 
            label={isEditing ? 'Nova Senha (deixe em branco para manter a atual)' : 'Senha*'}
            name="password"
            type="password"
            value={formData.password || ''}
            onChange={handleChange}
            required={!isEditing}
        />

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary">
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary">{isEditing ? 'Salvar Alterações' : 'Criar Usuário'}</Button>
        </div>
      </form>
    </Modal>
  );
};